import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import {Link as InfernoLink} from 'inferno-router';
import type {ILinkProps} from 'inferno-router/dist/Link';

import {document, window} from '../util';
import emitter from '../util/emitter';


interface LinkProps {
    to?: string;
    clickLabel?: string;
    disabled?: boolean;
    external?: boolean;
}

interface LinkxState {
    foo: number;
}

export class Link extends Component<LinkProps & ILinkProps & JSX.IntrinsicElements['a']> {
    originalOnClick?: Function;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        if (props.to) {
            props.to = this.cleanTo(props.to);
        }
    }
    componentDidMount() {
        this.setupRouterHack(this.props);
    }
    componentWillUnmount() {
        this.cleanupRouterHack();
    }
    componentWillUpdate(nextProps: jsx.Props, state: jsx.State) {
        if (nextProps.to) {
            nextProps.to = this.cleanTo(nextProps.to);
            if (this.props.to !== nextProps.to) {
                this.cleanupRouterHack();
                this.setupRouterHack(nextProps);
            }
        }
    }
    setupRouterHack(props: jsx.Props) {
        // have to use intervals since Inferno swallows the hashchange/popstate
        // events, but doesn't re-expose them via history events.
        // FIXME: This can't be good for performance.
        const id = this.getIdIfHashOnCurrentPage(props);
        if (id && process.env.BROWSER) {
            emitter.on('manual-hashchange', this.routerHandler);
        }
    }
    cleanupRouterHack() {
        process.env.BROWSER && emitter.off('manual-hashchange', this.routerHandler);
    }
    @autobind
    routerHandler() {
        // For some reason isActive isn't called on links for the same page.
        const id = this.getIdIfHashOnCurrentPage(this.props);
        if (id) {
            window.setTimeout(() => {
                this.setState({foo: Date.now()});
            }, 50);
        }
    }
    cleanTo(to: string): string {
        const shouldWarnUnlocalizedUrl =
            this.context.gtService.shouldWarnUnlocalizedUrl;
        if (shouldWarnUnlocalizedUrl(to)) {
            this.context.request.console.warn('Link.to is not localized:', to);
        }
        if (to.toString() !== to.trim()) {
            this.context.request.console.warn('Link.to is not trimmed:', to);
        }
        return to.trim();
    }
    getIdIfHashOnCurrentPage(props: jsx.Props): string|null {
        const {to} = props;
        if (!to) {
            return null;
        }
        const [pathWithoutHash, id] = to.split('#', 2);
        const isHash = id && (pathWithoutHash === '' ||
            pathWithoutHash.startsWith(
                this.context.router.history.location.pathname));
        return isHash ? id : null;
    }
    @autobind
    handleClick(event: Event) {
        const id = this.getIdIfHashOnCurrentPage(this.props);

        this.originalOnClick?.(event);
        const {to, clickLabel} = this.props;
        if (!to) {
            // Don't track click with no URL.
            return;
        }
        // We don't want to track "external" links to the same domain as being
        // outbound.
        if (to.charAt(0) !== '/' && !id) {
            window.gaEvent({
                category: 'outbound',
                action: 'click',
                label: to,
            });
        }
        if (clickLabel) {
            window.gaEvent({
                category: 'link',
                action: 'click',
                label: clickLabel,
            });
        }
        if (to.substr(0, 15) === 'riffsykeyboard:') {
            // If the page is still visible after a timeout, redirect to app
            // store.
            // TODO: log these users
            setTimeout(function handleClickTimeout() {
                if (!document.hidden) {
                    window.location.href = 'https://itunes.apple.com/app/apple-store/id917932200?pt=39040802&ct=Packs%20Promotion&mt=8';
                }
            }, 3000);
        }
    }
    @autobind
    isActive(): boolean {
        if (!window.location) {
            return false;
        }
        // This needs to use window.location, not router.history.location,
        // because router.history isn't updated with the latest hash.
        const {pathname, hash} = window.location;
        return this.props.to === hash || this.props.to === `${pathname}${hash}`;
    }

    render() {
        const CONSTANTS = this.context.CONSTANTS;
        let {
            clickLabel,
            onClick,
            external,
            disabled,
            ...props
        } = this.props;
        const emptyTo = !props.to;
        const isHash = !emptyTo && this.getIdIfHashOnCurrentPage(this.props);

        this.originalOnClick = onClick;
        (props as any).onClick = this.handleClick;

        if (!emptyTo && !props.to?.startsWith(`/${CONSTANTS.PATH}`) && !isHash) {
            external = true;
            // TODO: check if path matches current routes. This will help for
            // the main app, which has no path prefix.
        }

        if (disabled) {
            return <span {...props as JSX.IntrinsicElements['span']}/>;
        }

        const activeClassName = 'active';
        if (this.isActive()) {
            (props as any).className = `${props.className ? `${props.className} ` : ''}${activeClassName}`;
        }

        if (!isHash && !external && !emptyTo) {
            return <InfernoLink
                {...props as unknown as ILinkProps}
            />;
        }

        (props as any).href = props.to;
        // We don't want this rendered on the <a> element.
        delete (props as any).to;

        if (!isHash) {
            // For security: https://mathiasbynens.github.io/rel-noopener/
            (props as any).rel = 'noopener';
        }
        return <a {...props}/>;
    }
}
